import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft,
  Activity,
  Loader2,
  Save,
  TrendingUp,
  Play,
  Timer,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Target,
  ArrowRight,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLOR = {
  above_grade: '#059669',
  on_track: '#2563eb',
  approaching: '#d97706',
  needs_support: '#dc2626',
  not_recorded: '#94a3b8',
};

const STATUS_CLASS = {
  above_grade: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  on_track: 'bg-blue-100 text-blue-800 border-blue-300',
  approaching: 'bg-amber-100 text-amber-800 border-amber-300',
  needs_support: 'bg-red-100 text-red-800 border-red-300',
  not_recorded: 'bg-slate-100 text-slate-500 border-slate-300',
};

const STATUS_LABEL = {
  above_grade: 'Above grade',
  on_track: 'On track',
  approaching: 'Approaching',
  needs_support: 'Needs support',
  not_recorded: 'Not recorded',
};

const Sparkline = ({ points, cuts, width = 240, height = 60, color = '#2563eb' }) => {
  if (!points || points.length === 0) {
    return <div className="text-xs text-slate-400 italic">No sessions yet — record one below.</div>;
  }
  const validPoints = points.map((p, i) => ({ x: i, y: typeof p === 'number' ? p : 0 }));
  const maxY = Math.max(cuts?.above_grade || 100, ...validPoints.map((p) => p.y || 0), 10);
  const minY = 0;
  const stepX = validPoints.length > 1 ? (width - 20) / (validPoints.length - 1) : 0;
  const scaleY = (v) => height - 10 - ((v - minY) / (maxY - minY || 1)) * (height - 20);
  const path = validPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${10 + i * stepX},${scaleY(p.y || 0)}`)
    .join(' ');
  return (
    <svg width={width} height={height} className="w-full">
      {cuts && (
        <>
          <line x1="0" x2={width} y1={scaleY(cuts.on_track)} y2={scaleY(cuts.on_track)} stroke="#a7f3d0" strokeDasharray="3,3" />
          <line x1="0" x2={width} y1={scaleY(cuts.approaching)} y2={scaleY(cuts.approaching)} stroke="#fde68a" strokeDasharray="3,3" />
        </>
      )}
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {validPoints.map((p, i) => (
        <circle key={i} cx={10 + i * stepX} cy={scaleY(p.y || 0)} r="3.5" fill={color} />
      ))}
    </svg>
  );
};

// ============================================================================
// Guided in-app assessment modal
// ============================================================================
const GuidedAssessmentDialog = ({ open, onClose, childId, indicator, onSaved }) => {
  const { api } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [phase, setPhase] = useState('ready'); // ready | running | finished
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [correctCount, setCorrectCount] = useState(0);
  const [markedIndex, setMarkedIndex] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open || !indicator) return;
    setPhase('ready');
    setSecondsLeft(60);
    setCorrectCount(0);
    setMarkedIndex(new Set());
    api
      .get(`/skills/benchmarks/${childId}/assessment/${indicator}`)
      .then((r) => setAssessment(r.data))
      .catch(() => toast.error('Could not load assessment.'));
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, indicator, childId, api]);

  const startTimer = () => {
    setPhase('running');
    setSecondsLeft(assessment?.duration_seconds || 60);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setPhase('finished');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const stopEarly = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setPhase('finished');
  };

  const markItem = (idx) => {
    if (phase !== 'running') return;
    setMarkedIndex((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
        setCorrectCount((c) => Math.max(0, c - 1));
      } else {
        next.add(idx);
        setCorrectCount((c) => c + 1);
      }
      return next;
    });
  };

  const incrementManual = () => {
    if (phase !== 'running') return;
    setCorrectCount((c) => c + 1);
  };

  const saveScore = async () => {
    setSaving(true);
    try {
      await api.post(`/skills/benchmarks/${childId}`, {
        scores: { [indicator]: correctCount },
        note: `Guided ${indicator} assessment (in-app)`,
      });
      toast.success(`Saved ${indicator}: ${correctCount} ${assessment?.unit || 'items/min'}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const items = assessment?.items || [];
  const showItems = ['LNF', 'NWF', 'ORF'].includes(indicator);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="guided-assessment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-blue-500" />
            {indicator} — 60-second guided assessment
          </DialogTitle>
        </DialogHeader>
        {!assessment ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
              {assessment.instructions}
            </div>

            {phase === 'ready' && (
              <div className="text-center py-6">
                <p className="text-slate-600 mb-4">Ready when your child is. Tap Start to begin the 60-second timer.</p>
                <Button
                  onClick={startTimer}
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="assessment-start-btn"
                >
                  <Play className="w-5 h-5 mr-2" /> Start 60-second timer
                </Button>
              </div>
            )}

            {phase === 'running' && (
              <>
                <div className="sticky top-0 bg-white z-10 flex items-center justify-between border-b border-slate-200 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-3xl font-extrabold text-blue-600" data-testid="assessment-timer">
                      {secondsLeft}s
                    </div>
                    <span className="text-xs text-slate-500">left</span>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-600" data-testid="assessment-score">
                      {correctCount}
                    </div>
                    <span className="text-xs text-slate-500">correct</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={stopEarly} data-testid="assessment-stop-btn">
                    Stop early
                  </Button>
                </div>

                {showItems && items.length > 0 && (
                  <div className={indicator === 'ORF' ? 'text-lg leading-loose text-slate-800' : 'grid grid-cols-4 md:grid-cols-6 gap-2'}>
                    {items.map((item, idx) =>
                      indicator === 'ORF' ? (
                        <span
                          key={idx}
                          onClick={() => markItem(idx)}
                          className={`inline-block cursor-pointer mx-1 my-1 px-1 rounded ${
                            markedIndex.has(idx) ? 'bg-emerald-200 text-emerald-900' : 'hover:bg-blue-100'
                          }`}
                          data-testid={`assessment-item-${idx}`}
                        >
                          {item}
                        </span>
                      ) : (
                        <button
                          key={idx}
                          onClick={() => markItem(idx)}
                          className={`aspect-square rounded-lg border-2 text-2xl md:text-3xl font-bold transition ${
                            markedIndex.has(idx)
                              ? 'bg-emerald-500 text-white border-emerald-600'
                              : 'bg-white text-slate-800 border-slate-300 hover:border-blue-400'
                          }`}
                          data-testid={`assessment-item-${idx}`}
                        >
                          {item}
                        </button>
                      )
                    )}
                  </div>
                )}

                {!showItems && (
                  <div className="text-center py-6 space-y-3">
                    <p className="text-slate-600 text-sm">Say a word aloud and ask your child to segment it into sounds. Tap ✓ each time they say a phoneme correctly.</p>
                    <Button
                      size="lg"
                      onClick={incrementManual}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="assessment-manual-tally"
                    >
                      <Check className="w-6 h-6 mr-2" /> Correct (+1)
                    </Button>
                  </div>
                )}
              </>
            )}

            {phase === 'finished' && (
              <div className="text-center py-6 space-y-4">
                <p className="text-slate-500 text-sm">Time&apos;s up! Your child got</p>
                <div className="text-6xl font-black text-blue-600" data-testid="assessment-final-score">
                  {correctCount}
                </div>
                <p className="text-slate-600 text-sm">{assessment.unit}</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setPhase('ready')} data-testid="assessment-retry">
                    Try again
                  </Button>
                  <Button
                    onClick={saveScore}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="assessment-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save score
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Main page
// ============================================================================
const BenchmarksPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [growth, setGrowth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState({});
  const [saving, setSaving] = useState(false);
  const [guidedFor, setGuidedFor] = useState(null); // indicator string when open

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/skills/benchmarks/${childId}`).catch(() => null),
      api.get(`/skills/benchmarks/${childId}/history`).catch(() => null),
      api.get(`/skills/benchmarks/${childId}/growth`).catch(() => null),
    ])
      .then(([latest, hist, gr]) => {
        if (latest?.data) setData(latest.data);
        if (hist?.data) setHistory(hist.data.sessions || []);
        if (gr?.data) setGrowth(gr.data);
      })
      .catch(() => toast.error('Could not load benchmarks'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [childId]);

  const saveEntry = async () => {
    const scores = Object.fromEntries(
      Object.entries(entry)
        .filter(([, v]) => v !== '' && v !== null && v !== undefined)
        .map(([k, v]) => [k, Number(v)])
    );
    if (Object.keys(scores).length === 0) {
      toast.error('Enter at least one score.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/skills/benchmarks/${childId}`, { scores });
      toast.success('Benchmark saved!');
      setEntry({});
      load();
    } catch (err) {
      toast.error('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const followRecommendation = (rec) => {
    if (!rec?.route) return;
    const path = rec.route.replace('{child_id}', childId);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/learn/${childId}`)} data-testid="benchmarks-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="cursor-pointer" onClick={() => navigate(`/learn/${childId}`)}>
            <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2" data-testid="benchmarks-title">
              <Activity className="w-5 h-5 text-blue-500" /> Reading Benchmarks
            </h1>
            <p className="text-xs text-slate-500">Acadience-inspired fluency indicators + guided in-app assessments.</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {loading && (
          <div className="py-24 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        )}

        {!loading && data && (
          <>
            <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
              <span>
                Grade band: <span className="font-semibold text-slate-700">{data.band}</span>
                {data.band_source && (data.band_source.age || data.band_source.age_group || data.band_source.grade) && (
                  <span className="text-slate-400"> (based on {data.band_source.age ? `age ${data.band_source.age}` : data.band_source.grade ? `grade ${data.band_source.grade}` : `age group "${data.band_source.age_group}"`})</span>
                )}
              </span>
              {data.recorded_at && (
                <span>· Last recorded {new Date(data.recorded_at).toLocaleDateString()}</span>
              )}
              {growth && growth.days_since_last !== null && growth.days_since_last !== undefined && (
                <span>· {growth.days_since_last} days ago</span>
              )}
            </div>

            {/* Due-date banner */}
            {growth?.due_now && (
              <Card className="border border-amber-300 bg-amber-50" data-testid="benchmark-due-banner">
                <CardContent className="p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-amber-900">
                      {growth.sessions_recorded === 0
                        ? 'No benchmarks recorded yet.'
                        : `It's been ${growth.days_since_last}+ days — time for a fresh benchmark session.`}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      Acadience recommends BOY/MOY/EOY checkpoints (~every {growth.cadence_days} days). Use the guided 60-second assessments below.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Growth summary */}
            {growth?.deltas?.length > 0 && (
              <Card className="border border-slate-200 bg-gradient-to-br from-emerald-50 to-blue-50" data-testid="benchmark-growth-card">
                <CardContent className="p-5">
                  <p className="font-bold text-slate-900 flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-emerald-600" /> Growth over time
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {growth.deltas.map((d) => (
                      <div key={d.indicator} className="bg-white rounded-lg p-3 border border-slate-100" data-testid={`growth-${d.indicator}`}>
                        <p className="text-xs text-slate-500 font-semibold">{d.indicator}</p>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-2xl font-black text-slate-900">{d.latest}</span>
                          <span className="text-xs text-slate-400">from {d.first}</span>
                        </div>
                        <div
                          className={`flex items-center gap-1 mt-1 text-xs font-semibold ${
                            d.delta > 0 ? 'text-emerald-700' : d.delta < 0 ? 'text-red-700' : 'text-slate-500'
                          }`}
                        >
                          {d.delta > 0 ? <ArrowUp className="w-3 h-3" /> : d.delta < 0 ? <ArrowDown className="w-3 h-3" /> : null}
                          {d.delta > 0 ? '+' : ''}
                          {d.delta}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Focus recommendation */}
            {growth?.focus_recommendation && growth.focus_recommendation.status_latest !== 'above_grade' && (
              <Card className="border-2 border-blue-300 bg-blue-50" data-testid="benchmark-focus-card">
                <CardContent className="p-5">
                  <p className="font-bold text-slate-900 flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-600" /> Recommended focus
                  </p>
                  <p className="text-sm text-slate-700 mt-2">
                    <span className="font-semibold">{growth.focus_recommendation.indicator}</span> is currently{' '}
                    <span className="font-semibold">{STATUS_LABEL[growth.focus_recommendation.status_latest]}</span>. Try:
                  </p>
                  {growth.focus_recommendation.recommended && (
                    <Button
                      onClick={() => followRecommendation(growth.focus_recommendation.recommended)}
                      className="mt-3 bg-blue-600 hover:bg-blue-700"
                      data-testid="benchmark-focus-cta"
                    >
                      {growth.focus_recommendation.recommended.label} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-4" data-testid="benchmark-indicators">
              {data.indicators.map((ind) => {
                const seriesPoints = (history || [])
                  .map((row) => row.scores?.[ind.indicator])
                  .filter((v) => v !== null && v !== undefined);
                const color = STATUS_COLOR[ind.status] || STATUS_COLOR.not_recorded;
                return (
                  <Card key={ind.indicator} className="border border-slate-200">
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{ind.indicator}</p>
                          <p className="font-bold text-slate-800">{ind.label}</p>
                        </div>
                        <Badge className={`border ${STATUS_CLASS[ind.status] || STATUS_CLASS.not_recorded}`}>
                          {STATUS_LABEL[ind.status] || ind.status}
                        </Badge>
                      </div>
                      <div className="text-3xl font-extrabold text-slate-900">
                        {ind.score !== null && ind.score !== undefined ? ind.score : '—'}
                      </div>
                      <p className="text-xs text-slate-500">
                        Cut points: {ind.cuts.needs_support}/{ind.cuts.approaching}/{ind.cuts.on_track}/{ind.cuts.above_grade}
                      </p>
                      <div data-testid={`benchmark-sparkline-${ind.indicator}`} className="pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> Progress ({seriesPoints.length} sessions)
                          </span>
                        </div>
                        <Sparkline points={seriesPoints} cuts={ind.cuts} color={color} />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                        onClick={() => setGuidedFor(ind.indicator)}
                        data-testid={`benchmark-guided-${ind.indicator}`}
                      >
                        <Timer className="w-4 h-4 mr-1" /> Guided 60s assessment
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="border-2 border-dashed border-slate-300">
              <CardContent className="p-5 space-y-3">
                <p className="font-bold text-slate-800">Or record a manual session</p>
                <p className="text-xs text-slate-500">
                  Already timed your child externally? Enter the raw score (items correct per minute).
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {data.indicators.map((ind) => (
                    <div key={ind.indicator} className="space-y-1">
                      <label className="text-xs font-semibold text-slate-600">{ind.indicator}</label>
                      <Input
                        type="number"
                        value={entry[ind.indicator] ?? ''}
                        onChange={(e) => setEntry((p) => ({ ...p, [ind.indicator]: e.target.value }))}
                        placeholder="0"
                        data-testid={`benchmark-input-${ind.indicator}`}
                      />
                    </div>
                  ))}
                </div>
                <Button
                  onClick={saveEntry}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="benchmark-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save session
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <GuidedAssessmentDialog
        open={!!guidedFor}
        indicator={guidedFor}
        childId={childId}
        onClose={() => setGuidedFor(null)}
        onSaved={load}
      />
    </div>
  );
};

export default BenchmarksPage;
